'use client';
import React, { useEffect, useRef, useState } from 'react';
import { Typography, IconButton, MobileStepper, useMediaQuery, Fade } from '@mui/material';
import { KeyboardArrowLeft, KeyboardArrowRight } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { Slide } from '../../types/blocks/carousel';
import { CallToActionButton } from './call-to-action-button';
import Grid from '@mui/material/Grid2';

interface SwipableHomepageCarouselProps {
  slides: Slide[];
}

export const SwipableHomepageCarousel: React.FC<SwipableHomepageCarouselProps> = ({ slides }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [activeStep, setActiveStep] = useState(0);
  const [maxContentHeight, setMaxContentHeight] = useState(0);
  const contentRefs = useRef<(HTMLDivElement | null)[]>([]);
  const maxSteps = slides.length;

  useEffect(() => {
    if (!isMobile) return;

    const heights = contentRefs.current.map((ref) => ref?.offsetHeight || 0);
    setMaxContentHeight(Math.max(...heights));
  }, [slides, isMobile]);

  const handleNext = () => setActiveStep((prev) => prev + 1);
  const handleBack = () => setActiveStep((prev) => prev - 1);

  if (contentRefs.current.length !== slides.length) {
    contentRefs.current = Array(slides.length).fill(null);
  }

  return (
    <div className="max-w-[1264px] mx-auto px-4 md:px-[40px] lg:px-[88px] space-y-8 pt-8 pb-16 md:pt-16">
      <div className="flex flex-col justify-center w-full">
        <Grid
          container
          spacing={0}
          className="bg-paper rounded-xl overflow-hidden min-h-[400px] md:h-[650px]"
        >
          <Grid size={{ xs: 12, md: 6 }}>
            {!!slides[activeStep] && (
              <div
                ref={(el) => {
                  contentRefs.current[activeStep] = el;
                }}
                className="w-full h-full flex flex-col md:justify-center lg:m-auto py-10 px-8 sm:max-w-[550px] lg:max-w-[450px] lg:h-full gap-8 md:gap-6"
                style={{
                  height: isMobile ? maxContentHeight || '' : '',
                }}
              >
                <div className="flex flex-col w-full gap-y-2 sm:gap-y-4 md:gap-y-6">
                  <Typography variant="h1" component="h2">
                    {slides[activeStep].title}
                  </Typography>
                  <Typography
                    variant="s1"
                    component="p"
                    className="leading-6 md:leading-7 font-medium md:font-semibold"
                  >
                    {slides[activeStep].description}
                  </Typography>
                </div>
                <CallToActionButton button={slides[activeStep].callToAction} className="mr-auto" />
              </div>
            )}
          </Grid>

          <Grid size={{ xs: 12, md: 6 }} className="min-h-36 md:min-h-60 md:h-full">
            <Fade in timeout={500} key={activeStep + '-img'}>
              <div
                className="w-full h-full"
                style={{
                  backgroundImage: `url(${slides[activeStep].image?.url || ''})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'left',
                }}
              />
            </Fade>
          </Grid>
        </Grid>

        <div className="flex flex-wrap justify-center items-center mt-4 w-full">
          <MobileStepper
            className="gap-3"
            variant="dots"
            steps={maxSteps}
            position="static"
            activeStep={activeStep}
            nextButton={
              <IconButton
                size="large"
                color="primary"
                onClick={handleNext}
                disabled={activeStep === maxSteps - 1}
              >
                {theme.direction === 'rtl' ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
              </IconButton>
            }
            backButton={
              <IconButton
                size="large"
                color="primary"
                onClick={handleBack}
                disabled={activeStep === 0}
              >
                {theme.direction === 'rtl' ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
              </IconButton>
            }
          />
        </div>
      </div>
    </div>
  );
};
